import { z } from 'zod';
import { isValidTimeZone } from '../dates';
import { InstitutionRole, PolicyScope, WorkRole } from '../enums';
import { policyContentSchema } from '../policy/content';
import { emailSchema, nonEmptyText, optionalText, uuidSchema } from './common';

const slug = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(60)
  .regex(/^[a-z0-9-]+$/, 'Use letras minúsculas, números e hífen');

const timezone = z.string().refine(isValidTimeZone, 'Timezone IANA inválido (ex.: America/Sao_Paulo)');

export const createInstitutionSchema = z.object({
  name: nonEmptyText(200, 'Nome'),
  acronym: nonEmptyText(20, 'Sigla'),
  slug,
  timezone,
});
export type CreateInstitutionInput = z.infer<typeof createInstitutionSchema>;

export const updateInstitutionSchema = z.object({
  name: nonEmptyText(200, 'Nome').optional(),
  acronym: nonEmptyText(20, 'Sigla').optional(),
  timezone: timezone.optional(),
});
export type UpdateInstitutionInput = z.infer<typeof updateInstitutionSchema>;

export const programSchema = z.object({
  name: nonEmptyText(200, 'Nome'),
  code: nonEmptyText(30, 'Código'),
  description: optionalText(2000),
});
export type ProgramInput = z.infer<typeof programSchema>;

export const modalitySchema = z.object({
  key: z
    .string()
    .trim()
    .toUpperCase()
    .min(2)
    .max(40)
    .regex(/^[A-Z0-9_]+$/, 'Use letras, números e "_"'),
  name: nonEmptyText(120, 'Nome'),
  description: optionalText(2000),
});
export type ModalityInput = z.infer<typeof modalitySchema>;

const institutionRoleEnum = z.enum(Object.values(InstitutionRole) as [InstitutionRole, ...InstitutionRole[]]);
const workRoleEnum = z.enum(Object.values(WorkRole) as [WorkRole, ...WorkRole[]]);

export const memberRolesSchema = z.object({
  roles: z
    .array(z.object({ role: institutionRoleEnum, programId: uuidSchema.nullable().default(null) }))
    .min(1, 'Informe ao menos um papel')
    .max(10),
});
export type MemberRolesInput = z.infer<typeof memberRolesSchema>;

export const memberStatusSchema = z.object({ status: z.enum(['ACTIVE', 'SUSPENDED']) });

/**
 * Convite por link de uso único (qualquer papel): quem convida copia o link e o envia pelo canal
 * que preferir; o e-mail de acesso é informado pela pessoa convidada ao aceitar.
 */
export const createInvitationSchema = z
  .object({
    /** Opcional: somente a conta deste e-mail poderá aceitar o link. */
    email: emailSchema.optional(),
    /** Identificação para quem convida reconhecer o link (ex.: nome da pessoa). */
    label: optionalText(120),
    institutionId: uuidSchema,
    /** Convite institucional */
    institutionRole: institutionRoleEnum.optional(),
    programId: uuidSchema.nullable().optional(),
    /** Convite para trabalho (também cria filiação básica à instituição) */
    workId: uuidSchema.optional(),
    workRole: workRoleEnum.optional(),
    message: optionalText(1000),
  })
  .refine((v) => Boolean(v.institutionRole) !== Boolean(v.workId), {
    message: 'Informe um papel institucional OU um trabalho',
    path: ['institutionRole'],
  })
  .refine((v) => !v.workId || Boolean(v.workRole), { message: 'Informe o papel no trabalho', path: ['workRole'] });
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;

// ---------------------------------------------------------------- Políticas

const scopeEnum = z.enum(Object.values(PolicyScope) as [PolicyScope, ...PolicyScope[]]);

export const createPolicySchema = z
  .object({
    scope: scopeEnum.exclude(['WORK']),
    name: nonEmptyText(160, 'Nome'),
    institutionId: uuidSchema.nullable().optional(),
    programId: uuidSchema.nullable().optional(),
    modalityId: uuidSchema.nullable().optional(),
    description: optionalText(2000),
  })
  .superRefine((v, ctx) => {
    const need = (field: 'institutionId' | 'programId' | 'modalityId', msg: string) => {
      if (!v[field]) ctx.addIssue({ code: 'custom', path: [field], message: msg });
    };
    const forbid = (field: 'institutionId' | 'programId' | 'modalityId') => {
      if (v[field]) ctx.addIssue({ code: 'custom', path: [field], message: 'Não se aplica a este escopo' });
    };
    switch (v.scope) {
      case 'GLOBAL':
        forbid('institutionId');
        forbid('programId');
        break;
      case 'INSTITUTION':
        need('institutionId', 'Informe a instituição');
        forbid('programId');
        forbid('modalityId');
        break;
      case 'PROGRAM':
        need('institutionId', 'Informe a instituição');
        need('programId', 'Informe o programa');
        forbid('modalityId');
        break;
      case 'MODALITY':
        need('institutionId', 'Informe a instituição');
        need('modalityId', 'Informe a modalidade');
        break;
    }
  });
export type CreatePolicyInput = z.infer<typeof createPolicySchema>;

export const savePolicyDraftSchema = z.object({
  content: policyContentSchema,
  changeNote: optionalText(1000),
});
export type SavePolicyDraftInput = z.infer<typeof savePolicyDraftSchema>;

export const publishPolicySchema = z.object({ changeNote: optionalText(1000) });

export const resolvePolicyQuerySchema = z.object({
  institutionId: uuidSchema,
  programId: uuidSchema,
  modalityId: uuidSchema,
});
export type ResolvePolicyQuery = z.infer<typeof resolvePolicyQuerySchema>;
